import React, { useState, useEffect, useRef } from 'react';

import { useOutletContext, useNavigate, useLocation } from "react-router-dom";
import { useBreadStorageContext } from "contexts/BreadStorageContext";
import useTableInstance from "hooks/useTableInstance";
import useDataSourceManager from "hooks/useDataSourceManager";

import { v4 as uuidv4 } from "uuid";
import { createColumnHelper } from "@tanstack/react-table";

import { Grid, Box, CircularProgress, Tooltip, Typography, } from "@mui/material";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";

import TableField from "components/Table";
import SearchInput from "components/SearchInput";
import ModalContainer from 'components/ModalContainer';
import { SolidButton } from "components/buttons/SolidButton";

import ModalforCheck from '../modalCheck.js';
import ModalForAddOrgOrSite from '../ModalForAddOrgOrSite.js';

import { ReactComponent as AddIcon } from "assets/images/icons/icn_btn_add.svg";
import { formatTimestampToMinute } from "utils/format";
import EllipsisTooltipCell from "utils/ellipsisTooltipCell";

const checkMockup = process.env.REACT_APP_IS_MOCKUP;

const normalizeDeviceStatus = (devices, fallbackOnline = 0) => ({
  online: devices?.online ?? fallbackOnline,
  offline: devices?.offline ?? 0,
  onboarding: devices?.onboarding ?? 0,
});

const normalizeClientCounts = (clients) => ({
  wireless: clients?.wireless ?? (typeof clients === "number" ? clients : 0),
  wired: clients?.wired ?? 0,
});

const normalizeAlerts = (alerts) => ({
  critical: alerts?.critical ?? 0,
  normal: alerts?.normal ?? 0,
  low: alerts?.low ?? 0,
});

const formatCreatedTime = (createdAt) => {
  return formatTimestampToMinute(createdAt);
};

const SupportBadge = ({ badge }) => {
  const displayName = badge?.currentSupportDisplayName;

  if (!displayName) return null;

  return (
    <Tooltip title={displayName} arrow placement="top">
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          flexShrink: 1,
          minWidth: 0,
          maxWidth: "min(220px, 50%)",
          overflow: "hidden",
          height: "18px",
          ml: "7px",
          px: "7px",
          borderRadius: "9px",
          backgroundColor: "#DDF8DF",
          color: "#008A20",
          fontSize: "12px",
          fontWeight: 500,
          lineHeight: "18px",
          whiteSpace: "nowrap",
          boxSizing: "border-box",
        }}
      >
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          width: "13px",
          height: "13px",
          mr: "4px",
          borderRadius: "50%",
          backgroundColor: "#D9EDFF",
        }}
      >
        <ShieldOutlinedIcon sx={{ width: "10px", height: "10px", color: "#3B9BE7" }} />
      </Box>
      <Box
        component="span"
        sx={{
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {`Support · ${displayName}`}
      </Box>
      </Box>
    </Tooltip>
  );
};

const Organization = () => {
  const {scopeData, setScopeData, scopeReady} = useOutletContext();
  const location = useLocation();
  const isSurveillance =
    localStorage.getItem("selectedSystem") === "surveillance" ||
    location.pathname.startsWith("/surveillance");
  
  const { bread, setBread } = useBreadStorageContext();

  const [manageOrgsUuid, _setManageOrgsUuid] = useState(uuidv4());
  const [nowUuid, setNowUuid] = useState('');

  const { dataMap, sendRequest } = useDataSourceManager();
  const [supportBadges, setSupportBadges] = useState({});
  const [supportBadgeRequestMap, setSupportBadgeRequestMap] = useState({});
  const requestedSupportBadgesRef = useRef(new Set());
  const processedSupportBadgesRef = useRef(new Set());

  const [openModalForAddOrg, setOpenModalForAddOrg] = useState(false);
  const [modalForAddOrgTitle, setModalForAddOrgTitle] = useState('');
  const [modalForAddOrgCancelBtn, setModalForAddOrgCancelBtn] = useState('');
  const [modalForAddOrgSaveBtn, setModalForAddOrgSaveBtn] = useState('');
  
  const [openModalCheck, setOpenModalCheck] = useState(false);
  const [modalCheckTitle, setModalCheckTitle] = useState('');
  const [modalCheckContent, setModalCheckContent] = useState('');
  const [modalCheckCancelBtn, setModalCheckCancelBtn] = useState('');
  const [modalCheckSaveBtn, setModalCheckSaveBtn] = useState('');
  
  const [modalFrom, setModalFrom] = useState('');
  const [enableAction, setEnableAction] = useState(false);

  const getDatas = async () => {
    setNowUuid(manageOrgsUuid)

    let tmpSelectedBread = JSON.parse(localStorage.getItem('selectBreadcrumbs') || '{}');
    let tmpResource = '/ents/:entId/orgs.read';
    let tmpResourceAPIval = {entId: tmpSelectedBread.realm?.id }
    sendRequest( tmpResource, manageOrgsUuid, tmpResourceAPIval);
  };

  const mergeData= (orgList) => {
    let resultAry = []
    
    let manageOrgList = []
    if(orgList?.data?.orgs?.length > 0){
      manageOrgList = orgList.data.orgs
    }

    for(let childEnt of manageOrgList){
      const adoptedDevices = childEnt.adoptedDeviceNum ?? childEnt.adoptedDevices;
      let child = {
        id: childEnt.orgId,
        name: childEnt.orgName || childEnt.organizationName || childEnt.name || "-",
        roleId: childEnt.orgRoleId,
        roleName: childEnt.orgRoleName,
        siteCount: childEnt.siteNum ?? childEnt.siteCount ?? 0,
        adoptedDeviceCounts: {
          accessPoints: adoptedDevices?.ap ?? adoptedDevices?.accessPoints ?? 0,
          switches: adoptedDevices?.switch ?? adoptedDevices?.switches ?? 0,
          routers: adoptedDevices?.router ?? adoptedDevices?.routers ?? 0,
        },
        ...(isSurveillance
          ? {
            cameras: normalizeDeviceStatus(
              childEnt.cameraNum ?? childEnt.ipcamNum ?? childEnt.cameras,
              adoptedDevices?.camera ?? 0,
            ),
            nvrs: normalizeDeviceStatus(
              childEnt.nvrNum ?? childEnt.nvrs,
              adoptedDevices?.nvr ?? 0,
            ),
          }
          : {
            accessPoints: normalizeDeviceStatus(
              childEnt.accessPointNum ?? childEnt.apNum ?? childEnt.accessPoints,
              adoptedDevices?.accessPoints ?? 0,
            ),
            switches: normalizeDeviceStatus(
              childEnt.switchNum ?? childEnt.switches,
              adoptedDevices?.switches ?? 0,
            ),
          }),
        clients: normalizeClientCounts(childEnt.clientNum ?? childEnt.clients),
        alerts: normalizeAlerts(childEnt.alertNum ?? childEnt.alerts),
        createdTime: formatCreatedTime(childEnt.createdAt),
      }
      resultAry.push(child)
    }
    
    return resultAry;
  }

  const handleAddItem = () =>{
    if(!enableAction) return;
    setModalFrom('Add')
    setModalForAddOrgTitle('Add Organization')
    setModalForAddOrgCancelBtn('Cancel')
    setModalForAddOrgSaveBtn('Save')
    setOpenModalForAddOrg(true)
  }
  
  const handleScopeLink = (item) =>{
    let tmpSelectedBread = JSON.parse(localStorage.getItem('selectBreadcrumbs') || '{}');

    // save storage
    tmpSelectedBread.orgs = item;
    tmpSelectedBread.site = {};
    localStorage.setItem('selectBreadcrumbs', JSON.stringify(tmpSelectedBread));

    setBread(tmpSelectedBread)
    // navigate
    navigatePage(item);
  }

  const navigate = useNavigate();
  const navigatePage = () => {
    let path = "/org/sites";
    navigate(path);
  }

  const checkPermissions = () => {
    let tmpSelectedBread = JSON.parse(localStorage.getItem('selectBreadcrumbs') || '{}');
    if(tmpSelectedBread && Object.keys(tmpSelectedBread).length > 0){
      if(tmpSelectedBread.realm.roleId === 1 || tmpSelectedBread.realm.roleId === 2){
        setEnableAction(true)
      }else{
        setEnableAction(false)
      }
    }
  }

  useEffect(() => {
    checkPermissions()
  }, [bread]);

  useEffect(() => {
    if(scopeReady){
      checkPermissions()
    }
  }, [scopeReady]);

  function handleActionDenied(message) {
    setOpenModalForAddOrg(false);
    setModalCheckTitle('Action denied')
    setModalCheckContent(message)
    setModalCheckCancelBtn('')
    setModalCheckSaveBtn('OK')
    setModalFrom('error')
    setOpenModalCheck(true)
  }

  useEffect(() => {
    if(nowUuid !== ''){
      switch (nowUuid) {
        case manageOrgsUuid:
          if(dataMap[manageOrgsUuid]?.code !== undefined){
            setModalCheckTitle('Action denied')
            setModalCheckContent(dataMap[manageOrgsUuid].message)
            setModalCheckCancelBtn('')
            setModalCheckSaveBtn('OK')
            setModalFrom('error')
            setOpenModalCheck(true)
          }else if(dataMap[manageOrgsUuid] !== undefined){
            const result = mergeData(dataMap[manageOrgsUuid])
            setScopeData(prev => ({
              ...prev,
              orgs: result
            }));
          }

          setNowUuid('')
          break;
        default:
          break;
      }
    }
  }, [dataMap]);

  const columnHelper = createColumnHelper();
  const renderDeviceStatus = (devices) =>
    `${devices?.online ?? 0} | ${devices?.offline ?? 0} | ${
      devices?.onboarding ?? 0
    }`;
  const renderCountTooltip = (value, label) => (
    <Tooltip title={label} arrow placement="bottom">
      <Box component="span" sx={{ cursor: "default" }}>
        {value}
      </Box>
    </Tooltip>
  );

  const getAlertTotal = (alerts) =>
    (alerts?.critical ?? 0) + (alerts?.normal ?? 0) + (alerts?.low ?? 0);

  const networkColumns = [
    columnHelper.accessor("name", {
      header: "Organization",
      cell: (info) => (
        <Box sx={{ display: "flex", alignItems: "center", minWidth: 0, maxWidth: "100%", overflow: "hidden" }}>
          <EllipsisTooltipCell
            text={info.getValue()}
            onClick={() => handleScopeLink(info.row.original)}
            sx={{
              color: "#707ca8",
              cursor: "pointer",
              textDecoration: "underline",
              flex: "0 1 40%",
              minWidth: 0,
              maxWidth: "40%",
            }}
          />
          {renderSupportBadges(info.row.original.id)}
        </Box>
      ),
    }),
    columnHelper.accessor("siteCount", { header: "Sites" }),
    columnHelper.accessor("adoptedDeviceCounts.accessPoints", {
      header: "APs",
      cell: (info) => info.getValue() ?? 0,
    }),
    columnHelper.accessor("adoptedDeviceCounts.switches", {
      header: "Switches",
      cell: (info) => info.getValue() ?? 0,
    }),
    columnHelper.accessor("adoptedDeviceCounts.routers", {
      header: "Gateways",
      cell: (info) => info.getValue() ?? 0,
    }),
    columnHelper.accessor("alerts", {
      header: "Alerts",
      cell: (info) => getAlertTotal(info.getValue()),
    }),
    columnHelper.accessor("daysLeft", {
      header: "Days left",
      cell: (info) => Number(info.getValue()) || 0,
    }),
  ];

  const surveillanceColumns = [
    columnHelper.accessor("name", {
      header: "Name",
      cell: (info) => (
        <Box sx={{ display: "flex", alignItems: "center", minWidth: 0, maxWidth: "100%", overflow: "hidden" }}>
          <EllipsisTooltipCell
            text={info.getValue()}
            onClick={() => handleScopeLink(info.row.original)}
            sx={{
              color: "#707ca8",
              cursor: "pointer",
              textDecoration: "underline",
              flex: "0 1 40%",
              minWidth: 0,
              maxWidth: "40%",
            }}
          />
          {renderSupportBadges(info.row.original.id)}
        </Box>
      ),
    }),
    columnHelper.accessor("roleName", { header: "Role" }),
    columnHelper.accessor("siteCount", { header: "Site" }),
    columnHelper.accessor("cameras", {
      header: "Camera",
      cell: (info) => renderCountTooltip(
        renderDeviceStatus(info.getValue()),
        "Online | Offline | Onboarding",
      ),
    }),
    columnHelper.accessor("nvrs", {
      header: "NVR",
      cell: (info) => renderCountTooltip(
        renderDeviceStatus(info.getValue()),
        "Online | Offline | Onboarding",
      ),
    }),
    columnHelper.accessor("createdTime", { header: "Created Time" }),
  ];

  const columnsData = isSurveillance ? surveillanceColumns : networkColumns;

  const orgData = Array.isArray(scopeData?.orgs) ? scopeData.orgs : [];

  useEffect(() => {
    if (isSurveillance || orgData.length === 0) return;

    let supportSession = null;
    try {
      supportSession = JSON.parse(sessionStorage.getItem("supportSession") || "null");
    } catch (error) {
      console.error("[SupportBadge] Invalid supportSession:", error);
    }
    if (supportSession?.supportTokenId) return;

    let entId = "";
    try {
      const breadcrumbs = JSON.parse(localStorage.getItem("selectBreadcrumbs") || "{}");
      entId = breadcrumbs?.realm?.id || "";
    } catch (error) {
      console.error("[SupportBadge] Failed to read selected enterprise:", error);
    }
    if (!entId) return;

    orgData.forEach((org) => {
      const orgId = org?.id;
      if (!orgId || requestedSupportBadgesRef.current.has(orgId)) return;

      requestedSupportBadgesRef.current.add(orgId);
      const requestUuid = `support-${uuidv4()}`;
      setSupportBadgeRequestMap((previous) => ({
        ...previous,
        [requestUuid]: orgId,
      }));
      sendRequest(
        "/ents/:entId/orgs/:orgId/supportTokens/badges.read",
        requestUuid,
        { entId, orgId },
      );
    });
  }, [orgData, isSurveillance, sendRequest]);

  useEffect(() => {
    Object.entries(supportBadgeRequestMap).forEach(([requestUuid, orgId]) => {
      if (processedSupportBadgesRef.current.has(requestUuid)) return;
      const response = dataMap[requestUuid];
      if (!response) return;

      processedSupportBadgesRef.current.add(requestUuid);
      const items =
        response?.result?.data?.items ||
        response?.data?.items ||
        response?.items ||
        [];
      const activeBadges = Array.isArray(items)
        ? items.filter((item) => item?.status === "active" && item?.currentSupportDisplayName)
        : [];

      setSupportBadges((previous) => ({
        ...previous,
        [orgId]: activeBadges,
      }));
    });
  }, [dataMap, supportBadgeRequestMap]);

  const renderSupportBadges = (orgId) =>
    (supportBadges[orgId] || []).map((badge) => (
      <SupportBadge key={badge.supportTokenId || badge.currentSupportDisplayName} badge={badge} />
    ));

  const table = useTableInstance({
    tableName: "hierarchyRealmOrg",
    data: orgData,
    columns: columnsData,
    enableSorting: true,
    enablePagination: true,
    enableGlobalFilter: true,
    enableRowSelection: true,
  });

  const emptyHead = useTableInstance({
    tableName: "emptyRealmOrg",
    data: '',
    columns: columnsData,
    enableSorting: true,
  });

  return (
      <>
        {!isSurveillance && !scopeReady ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 240 }}>
            <CircularProgress />
          </Box>
        ) : (
        <div className={`main-card-content`}
          style={{marginTop: '60px', flex: (orgData.length>0)?'':'1',
          display:'flex', flexDirection:'column'}}>
          <div>
            <Grid container style={{justifyContent:'space-between', marginBottom:'10px'}}>
              <Box style={{display:'flex'}}>
                <Grid container sx={{alignItems: "center",}}>
                  <Typography fontSize="16px" fontWeight="600" color="rgba(0, 0, 0, 0.8)">
                    Organizations
                  </Typography>
                </Grid>
              </Box>
              <Box style={{display:'flex'}}>
                { orgData.length>0 && <Grid container sx={{alignItems: "center", gap:'15px'}}>
                  <SearchInput
                    value={table.globalFilter}
                    onChange={table.setGlobalFilter}
                  />
                  {enableAction && 
                    <SolidButton 
                      disabled={enableAction? false: true}
                      startIcon={<AddIcon />}
                      height="32px"
                      onClick={() => handleAddItem()}
                    >
                      Add Organization
                    </SolidButton>
                  }
                </Grid> }
              </Box>
            </Grid>
          </div>
          {orgData.length > 0 &&
            <TableField
              {...table}
            />
          }
          { orgData.length === 0 && <>
            <TableField
              {...emptyHead}
            />    
            <div style={{flex:'1',alignContent:'center', justifyItems:'center'}}>
              <Grid container style={{justifyContent:'center', marginBottom:'40px'}}>
                <Typography sx={{textAlign:'center'}}
                fontSize="16px" fontWeight="600" color="rgba(0, 0, 0, 0.8)">
                  No entry in the table, you can create a new organization.
                </Typography>
              </Grid>
              <Box>
                {enableAction && 
                  <SolidButton 
                    disabled={enableAction? false: true}
                    startIcon={<AddIcon />}
                    height="32px"
                    onClick={() => handleAddItem()}
                  >
                    Add Organization
                  </SolidButton>
                }
              </Box>
            </div> 
          </>}
        </div>
        )}
        <ModalContainer
          display={openModalCheck}
          modalWidth={'500px'} //550px, 750px, empty=1000px,
          children={
            <ModalforCheck
              title={modalCheckTitle}
              content={modalCheckContent}
              cancelBtn={modalCheckCancelBtn}
              saveBtn={modalCheckSaveBtn}
              setOpenModal={setOpenModalCheck}
              setModalAction={() => {}}
            />
          }
        />
        <ModalForAddOrgOrSite
          auth="Organization"
          display={openModalForAddOrg}
          title={modalForAddOrgTitle}
          setOpenModal={setOpenModalForAddOrg}
          cancelBtn={modalForAddOrgCancelBtn}
          saveBtn={modalForAddOrgSaveBtn}
          errorHandling={handleActionDenied}
          refresh={getDatas}
        />
      </>
  );
};

export default Organization;
